import type { Dizionario } from './it';

type Traduzione<T> = {
  [K in keyof T]: T[K] extends string ? string : T[K] extends readonly string[] ? readonly string[] : Traduzione<T[K]>;
};

/** English dictionary, typed on the Italian one: a missing key fails compilation. */
export const en: Traduzione<Dizionario> = {
  app: { nome: 'Ema Comfort', poweredBy: 'Ema Comfort platform', versione: 'Version', caricamento: 'Loading the installation…' },
  nav: {
    pannello: 'Panel',
    zone: 'Zones',
    programmazione: 'Schedule',
    storico: 'History',
    impostazioni: 'Settings',
    notifiche: 'Notifications',
    sanitario: 'Hot water',
    ventilazione: 'Ventilation',
    tecnico: 'Technical area',
    principale: 'Main navigation',
  },
  stato: {
    sistema: 'System status',
    attivo: 'Active',
    spento: 'Off',
    connesso: 'Connected',
    lan: 'LAN',
    cloud: 'Cloud',
    degradato: 'Partial',
    irraggiungibile: 'Controller unreachable',
    datiObsoleti: 'Data not current',
    ultimaLettura: 'Last valid reading: {{ora}}',
    nessunaLettura: 'No reading received',
    nonDisponibile: 'Not available',
    sondaScollegata: 'Sensor disconnected',
    aggiornatoOra: 'Updated now',
    aggiornatoAlle: 'Updated at {{ora}}',
    ultimoDato: 'Last data: {{quando}}',
    solaLettura: 'Read only',
    sorgenteCaduta: 'Source unreachable',
    bannerIrraggiungibile: 'The controller is not responding. Values shown are not current; commands are not sent.',
    bannerSolaLettura: 'Read-only mode',
  },
  modalita: {
    titolo: 'Operating mode',
    RISCALDAMENTO: 'Heating',
    RAFFRESCAMENTO: 'Cooling',
    AUTOMATICO: 'Automatic',
    SPENTO: 'Off',
    attuale: 'Current mode: {{modalita}}',
  },
  zone: {
    titolo: 'Zones',
    principali: 'Main settings',
    individuali: 'Individual zone temperatures',
    attiva: 'On',
    spenta: 'Off',
    tempDesiderata: 'Target temp.',
    tempAttuale: 'Current',
    accendi: 'Turn on {{zona}}',
    spegni: 'Turn off {{zona}}',
    globale: 'Global temperature',
    globaleNota: 'Applies to zones not set individually.',
    piu: 'Raise {{zona}} by half a degree',
    meno: 'Lower {{zona}} by half a degree',
    inDomanda: 'Calling',
    aRegime: 'Satisfied',
    limite: 'Limit {{min}}–{{max}} set by {{chi}}',
    fuoriLimite: 'Not sent: the {{min}}–{{max}} limit was set by {{chi}}.',
    sovrascritta: 'Set individually',
    nessuna: 'No zones configured: the controller declared no room sensors.',
    carico: { ELETTROVALVOLA: 'Zone valve', RELE_ON_OFF: 'On/off relay', VENTILCONVETTORE: 'Fan coil' },
  },
  comando: {
    inInvio: 'Sending…',
    accettato: 'Accepted',
    applicato: 'Applied',
    fallito: 'Not applied',
    motivo: {
      centralina_irraggiungibile: 'The controller did not respond.',
      fuori_limiti: 'Value outside the allowed limits.',
      sorgente_non_comandabile: 'This source is read-only.',
      zona_inesistente: 'Zone not found.',
      timeout: 'No confirmation within the expected time.',
      rifiutato: 'Command rejected by the controller.',
    },
  },
  pannello: {
    titolo: 'Panel',
    ambiente: 'Indoor',
    centraleTermica: 'Plant room',
    mandata: 'Flow temperature',
    ritorno: 'Return temperature',
    deltaT: 'Delta T',
    accumulo: 'Buffer tank',
    solaLettura: 'Read-only plant: this source publishes readings but accepts no commands.',
    centraleSenzaSonde: 'This source publishes no plant-room temperatures: flow, return and buffer are not measured.',
    accumuloSenzaSonda: 'No sensor on the buffer',
    accumuloSenzaSondaNota: 'The value is not estimated: the I/O map has no buffer sensor.',
    pufferAlto: 'Top',
    pufferBasso: 'Bottom',
    sanitario: 'Hot water',
    esterna: 'Outdoor temperature',
    umidita: 'Humidity',
    luminosita: 'Light level',
    circolatore: 'Pump',
    acceso: 'Running',
    spento: 'Off',
    generatori: 'Heat sources',
    impianto: 'Heating circuit',
    zoneAttive: '{{n}} of {{tot}} zones calling',
    mediaAmbiente: 'Room temperature',
    programmazioneOraria: 'Schedule',
    sorgenti: 'Data sources',
    energia: {
      titolo: 'Energy',
      produzione: 'Production',
      consumo: 'Consumption',
      batteria: 'Battery',
      rete: 'Grid',
      prelievo: 'Importing from grid',
      immissione: 'Exporting to grid',
      carica: 'Charging',
      scarica: 'Discharging',
      surplusTitolo: 'Solar surplus',
      surplusDisponibile: '{{kw}} kW surplus available',
      surplusNessuno: 'No surplus right now',
      versoPdc: 'Could run the heat pump and charge the buffer and hot water.',
      comandoNonDisponibile: 'Command not available: the heat pump connector is read-only (phase 2).',
      comandoDisponibile: 'Command available on the heat source.',
      fase2: 'Phase 2',
    },
  },
  generatore: {
    TERMOCAMINO: 'Wood fireplace',
    STUFA_PELLET: 'Pellet stove',
    POMPA_DI_CALORE: 'Heat pump',
    CALDAIA_GAS: 'Gas boiler',
    priorita: 'Priority {{n}}',
    mandata: 'Flow',
    ritorno: 'Return',
    modulante: 'Modulating',
    onOff: 'On/off',
  },
  sanitario: {
    titolo: 'Domestic hot water',
    accumulo: 'Tank temperature',
    setpoint: 'Target temperature',
    boost: 'Boost',
    boostAttivo: 'Boost active until {{ora}}',
    boostAvvia: 'Start boost ({{minuti}} min)',
    boostAnnulla: 'Cancel boost',
    inCarica: 'Heating',
    aRegime: 'At temperature',
    accesa: 'On',
    spenta: 'Off',
    solaLetturaNota: 'Branded thermostats via the manufacturer’s cloud: read-only status.',
    antilegionella: 'Anti-legionella cycle: {{giorno}} at {{ora}}:00 to {{temp}}',
    nonPresente: 'This installation has no hot water tank connected.',
    piu: 'Raise hot water temperature',
    meno: 'Lower hot water temperature',
  },
  vmc: {
    titolo: 'Mechanical ventilation',
    modalita: { SPENTA: 'Off', ACCESA: 'On', AUTO: 'Automatic' },
    umidita: 'Indoor humidity',
    soglia: 'In automatic mode it runs above {{pct}}% humidity.',
    attiva: 'Running',
    inattiva: 'Stopped',
    nonPresente: 'This installation has no mechanical ventilation connected.',
  },
  programmazione: {
    titolo: 'Schedule',
    zona: 'Zone',
    fasce: 'Time slots',
    aggiungi: 'Add slot',
    rimuovi: 'Remove',
    da: 'From',
    a: 'To',
    setpoint: 'Temperature',
    attivo: 'Schedule active',
    disattivo: 'Schedule disabled',
    attivoNota: 'Outside the slots the temperature set in Zones applies.',
    profili: 'Quick profiles',
    profilo: { CASA: 'Home', FUORI: 'Away', NOTTE: 'Night', VACANZA: 'Holiday' },
    applicaProfilo: 'Apply the “{{profilo}}” profile to all zones ({{temp}})?',
    applicaProfiloNota: 'Individually set temperatures will be replaced.',
    salva: 'Save schedule',
    salvato: 'Schedule saved',
    nessunaFascia: 'No slots on this day.',
    fasceNonValide: 'Slots overlap or have inverted times.',
    confermaRimozione: 'Remove the {{da}}–{{a}} slot?',
    inCorso: 'Now',
    copiaSuFeriali: 'Copy to weekdays',
  },
  storico: {
    titolo: 'History',
    giorno: 'Day',
    settimana: 'Week',
    mese: 'Month',
    esporta: 'Export CSV',
    serie: { zone: 'Zones', esterna: 'Outdoor', mandata: 'Flow', ritorno: 'Return', energia: 'Energy', produzione: 'Production', consumo: 'Consumption' },
    nota: 'Series aggregated by the controller at the requested resolution: {{risoluzione}}.',
    esportaNota: 'The file carries the installation header, period and recorded quantities.',
    risoluzione: { '1m': '1 minute', '15m': '15 minutes', '1h': '1 hour', '1d': '1 day' },
    nessunDato: 'No data in the period.',
    temperatura: 'Temperature',
    potenza: 'Power',
  },
  notifiche: {
    titolo: 'Notifications',
    aperti: 'Open',
    tutti: 'All',
    riconosci: 'Acknowledge',
    riconosciuto: 'Acknowledged by {{chi}} at {{ora}}',
    chiuso: 'Cleared at {{ora}}',
    nessuna: 'No alarms. All good.',
    severita: { bassa: 'Low', media: 'Medium', alta: 'High', critica: 'Critical' },
    codice: {
      SONDA_GUASTA: 'Sensor fault',
      SONDA_SCOLLEGATA: 'Sensor disconnected',
      DELTA_T_ANOMALO: 'Abnormal delta T',
      SOVRATEMPERATURA: 'Over-temperature',
      GENERATORE_NON_PARTE: 'Heat source failed to start',
      CENTRALINA_IRRAGGIUNGIBILE: 'Controller unreachable',
      SORGENTE_IRRAGGIUNGIBILE: 'Source unreachable',
      PUNTO_DI_RUGIADA: 'Dew point protection',
    },
    descrizione: {
      SONDA_GUASTA: 'Sensor {{oggetto}} returns implausible values.',
      SONDA_SCOLLEGATA: 'Sensor {{oggetto}} is not connected: the value is unavailable.',
      DELTA_T_ANOMALO: 'Flow/return difference too low with the pump running.',
      SOVRATEMPERATURA: 'Flow exceeded the safety limit: heat source locked out.',
      GENERATORE_NON_PARTE: 'The heat source did not start after the request.',
      CENTRALINA_IRRAGGIUNGIBILE: 'No reading received for over a minute.',
      SORGENTE_IRRAGGIUNGIBILE: 'Source “{{oggetto}}” is not responding: its tiles show the last data available.',
      PUNTO_DI_RUGIADA: 'Cooling flow was raised to avoid condensation on the floor.',
    },
  },
  impostazioni: {
    titolo: 'Settings',
    lingua: 'Language',
    unita: 'Temperature unit',
    tema: 'Appearance',
    temaScuro: 'Dark',
    temaChiaro: 'Light',
    rete: 'Network',
    reteLan: 'Controller on LAN · {{host}}',
    reteCloud: 'Connected via cloud',
    reteNota: 'The panel talks to the controller over the home network: it works without internet.',
    account: 'Account',
    accountNota: 'Signing out ends the session on every product that shares this sign-in.',
    esci: 'Sign out',
    residente: 'Resident',
    areaTecnica: 'Technical area',
    areaTecnicaNota: 'For the installer only: requires dedicated credentials.',
    accedi: 'Open technical area',
    informazioni: 'About',
    impianto: 'Installation',
    centralina: 'Controller',
    firmware: 'Firmware',
    installatore: 'Installer',
    demo: {
      titolo: 'Demonstration',
      nota: 'These controls exist only in the simulator: they show how the interface reacts.',
      marchio: 'Brand',
      marchioNota: 'The panel carries the installer’s brand, not the platform’s.',
      scenari: 'Scenarios',
      centralinaIrraggiungibile: 'Controller unreachable',
      sondaScollegata: 'Sensor {{canale}} disconnected',
      sorgenteGiu: 'Source “{{sorgente}}” unreachable',
      velocita: 'Simulation speed',
      velocitaValore: '{{x}}×',
      impianto: 'Installation shown',
    },
  },
  tecnico: {
    titolo: 'Technical area',
    accesso: {
      titolo: 'Technical area sign-in',
      sottotitolo: 'Credentials distinct from the resident’s. The session expires after 10 minutes of inactivity.',
      utente: 'User',
      password: 'Password',
      entra: 'Sign in',
      errore: 'Invalid credentials.',
      demoHint: 'Demo: user “tecnico”, password “emacomfort”.',
      sessioneScaduta: 'Session expired due to inactivity.',
      esci: 'Sign out',
      sessione: '{{operatore}} · expires in {{minuti}} min',
      tornaAlPannello: 'Back to the panel',
    },
    nav: {
      parco: 'Installations',
      dashboard: 'Channels',
      io: 'I/O configuration',
      parametri: 'Parameters',
      generatori: 'Heat sources',
      diagnostica: 'Diagnostics',
      firmware: 'Firmware',
      registro: 'Service log',
      titolo: 'Technical area navigation',
    },
    parco: {
      titolo: 'Installations',
      sottotitolo: 'Every installation managed by {{installatore}}',
      impianti: 'Installations',
      online: 'Online',
      offline: 'Unreachable',
      degradati: 'Partial',
      allarmi: 'Open alarms',
      apri: 'Open',
      inUso: 'In use',
      ultimoContatto: 'Last contact',
      mai: 'never',
      zone: '{{n}} zones',
      generatori: '{{n}} heat sources',
      centralina: { emacomfort: 'Ema Comfort controller', terze_parti: 'Third-party systems' },
      firmware: 'Firmware {{v}}',
    },
    canali: {
      titolo: 'Live channels',
      sottotitolo: 'Raw and converted value of every channel declared by the controller.',
      canale: 'Channel',
      assegnazione: 'Assignment',
      grezzo: 'Raw',
      convertito: 'Converted',
      qualita: 'Quality',
      modulo: 'Module',
      ingressi: 'Inputs',
      uscite: 'Outputs',
      nonAssegnato: 'Unassigned',
      confidenza: { alta: 'Confirmed', media: 'To confirm', bassa: 'Uncertain' },
      qualitaValore: { ok: 'Valid', sonda_scollegata: 'Sensor disconnected', sospetto: 'Suspect', mancante: 'Missing' },
      stato: 'State',
      modulazione: 'Modulation',
      forzato: 'Forced until {{ora}}',
      moduli: 'Declared modules',
      moduloStato: { ok: 'Present', assente: 'Absent', guasto: 'Faulty' },
    },
    io: {
      titolo: 'I/O configuration',
      sottotitolo: 'Channel → zone or function mapping, sensor type, calibration, load type per zone, output tests.',
      assegnaA: 'Assign to',
      zona: 'Zone',
      funzione: 'Function',
      nessuna: 'None',
      tipoSonda: 'Sensor type',
      offset: 'Offset',
      fattore: 'Factor',
      salva: 'Save channel',
      salvato: 'Channel {{canale}} saved',
      tipoCarico: 'Load type per zone',
      tipoCaricoNota: 'Zone valves, on/off relays and fan coils behave differently: the controller adapts the command.',
      testUscita: 'Output test',
      forzaAttivo: 'Force ON',
      forzaSpento: 'Force OFF',
      timeout: 'Automatic release after',
      timeoutNota: 'The timeout is enforced by the controller: if the connection drops, the output still releases.',
      secondi: '{{n}} s',
      avviaForzatura: 'Start override',
      annullaForzatura: 'Cancel override',
      forzaturaAttiva: 'Override active · releases at {{ora}}',
      confermaForzatura: 'Force output {{canale}} to {{stato}} for {{secondi}} seconds?',
      nota: 'Note',
      sonda: { NTC_10K: 'NTC 10K', PT1000: 'PT1000', ANALOGICO: 'Analogue', DIGITALE: 'Digital (connector)' },
    },
    parametri: {
      titolo: 'Control parameters',
      sottotitolo: 'Technical settings per installation. Every change is logged with operator, time and before/after values.',
      avviso: 'Defaults are an assumption pending the client’s control algorithm.',
      salva: 'Save parameters',
      salvato: 'Parameters saved',
      nonValidi: 'Some values are outside the allowed range.',
      gruppi: {
        curvaClimatica: 'Weather compensation curve',
        isteresi: 'Hysteresis',
        deltaT: 'Delta T',
        circolatore: 'Pump',
        sanitario: 'Hot water',
        cascata: 'Heat source cascade',
        comportamentoPin12: 'Heat source actuator behaviour (pin 12)',
        protezioni: 'Protections',
        commutazioneStagionale: 'Seasonal changeover',
        vmc: 'Ventilation',
      },
      campi: {
        pendenza: 'Slope',
        temperaturaBase: 'Base temperature',
        temperaturaEsternaProgetto: 'Design outdoor temperature',
        mandataMin: 'Minimum flow',
        mandataMax: 'Maximum flow',
        zona: 'Zone hysteresis',
        generatore: 'Heat source hysteresis',
        tempoMinimoOnMin: 'Minimum on time',
        tempoMinimoOffMin: 'Minimum off time',
        nominale: 'Nominal delta T',
        sogliaAllarme: 'Alarm threshold',
        minutiPrimaDellAllarme: 'Minutes before alarm',
        modalita: 'Mode',
        portataMinimaPct: 'Minimum flow rate',
        setpoint: 'DHW setpoint',
        isteresi: 'DHW hysteresis',
        prioritaSuRiscaldamento: 'Priority over heating',
        tettoPrioritaMin: 'Priority cap',
        antilegionellaTemperatura: 'Anti-legionella: temperature',
        antilegionellaGiorno: 'Anti-legionella: day',
        antilegionellaOra: 'Anti-legionella: hour',
        ritardoSecondarioMin: 'Secondary start delay',
        antigeloZona: 'Zone frost protection',
        antigeloEsterna: 'Outdoor frost protection',
        sovratemperaturaDelta: 'Over-temperature (above curve)',
        antibloccaggioGiorni: 'Pump anti-seize',
        margineRugiada: 'Dew point margin',
        sogliaRiscaldamento: 'Below this outdoor temp: heating',
        sogliaRaffrescamento: 'Above this outdoor temp: cooling',
        ritardoOre: 'Changeover delay',
        sogliaUmidita: 'Humidity threshold',
      },
      unita: { gradi: '°C', minuti: 'min', ore: 'h', giorni: 'days', pct: '%', kelvinPerGrado: '°C/°C' },
      circolatoreModalita: { FISSO: 'Fixed', MODULANTE: 'Modulating' },
      pin12Nota: 'Chosen from a described list, never a bare number: it must still be readable in two years.',
    },
    generatori: {
      titolo: 'Heat sources',
      sottotitolo: 'Declaration of installed heat sources (1–4), associated channels and cascade priority.',
      aggiungi: 'Add heat source',
      rimuovi: 'Remove',
      nome: 'Name',
      tipo: 'Type',
      canaleMandata: 'Flow channel',
      canaleRitorno: 'Return channel',
      canaleComando: 'Command channel',
      nessunComando: 'None',
      modulante: 'Modulating',
      su: 'Raise priority',
      giu: 'Lower priority',
      salva: 'Save heat sources',
      salvato: 'Heat sources saved',
      massimo: 'Four at most: wood fireplace, pellet stove, heat pump, gas boiler.',
      cascataNota: 'Priority is set by the technician, not inferred: a fireplace and a heat pump have opposite economics.',
      attivo: 'Running',
      inattivo: 'Idle',
    },
    diagnostica: {
      titolo: 'Diagnostics',
      sottotitolo: 'High-resolution flow / return / outdoor comparison and controller events.',
      ultime24h: 'Last 24 hours',
      eventi: 'Recent events',
      nessunEvento: 'No events recorded.',
      esporta: 'Export CSV',
      tipo: {
        generatore: 'Heat source',
        sanitario: 'Hot water',
        vmc: 'Ventilation',
        modalita: 'Mode',
        profilo: 'Profile',
        allarme: 'Alarm',
        forzatura: 'Override',
        protezione: 'Protection',
        configurazione: 'Configuration',
        connessione: 'Connection',
        ota: 'Firmware',
      },
    },
    firmware: {
      titolo: 'Firmware / OTA',
      corrente: 'Installed version',
      disponibile: 'Update available',
      nessuno: 'No update available.',
      terzeParti: 'Third-party controller: its firmware is not managed by Ema Comfort.',
      avvia: 'Start update {{v}}',
      rollback: 'Roll back to {{v}}',
      testNonFirmato: 'Try an unsigned image',
      testNonFirmatoNota: 'Shows that the controller rejects firmware without a valid signature.',
      fase: {
        inattivo: 'Idle',
        pubblicazione: 'Publishing',
        rollout: 'Rolling out',
        conferma: 'Awaiting confirmation',
        completato: 'Completed',
        rifiutato: 'Rejected',
        rollback: 'Rolling back',
      },
      firmata: 'Signed image',
      nonFirmata: 'UNSIGNED image',
      rifiutatoNota: 'The controller rejected the image: signature missing or invalid.',
      confermaAvvio: 'Start the update to version {{v}} on this installation?',
    },
    registro: {
      titolo: 'Service log',
      sottotitolo: 'Append-only: no entry can be edited or deleted.',
      operatore: 'Operator',
      azione: 'Action',
      oggetto: 'Subject',
      prima: 'Before',
      dopo: 'After',
      istante: 'Time',
      vuoto: 'No service actions recorded.',
      azioni: {
        accesso_area_tecnica: 'Technical area sign-in',
        taratura_offset: 'Calibration: offset',
        taratura_fattore: 'Calibration: factor',
        tipo_sonda: 'Sensor type',
        assegnazione_canale: 'Channel assignment',
        tipo_carico: 'Load type',
        parametro: 'Parameter',
        generatori: 'Heat sources',
        forzatura: 'Output override',
        rientro_forzatura: 'Override released (timeout)',
        annulla_forzatura: 'Override cancelled',
        ota_avvio: 'Update started',
        ota_rifiutato: 'Update rejected',
        ota_rollback: 'Firmware rollback',
      },
    },
    funzione: {
      MANDATA_COLLETTORE: 'Manifold flow',
      RITORNO_COLLETTORE: 'Manifold return',
      ACCUMULO_SANITARIO: 'Hot water tank',
      MANDATA_GENERATORE: 'Heat source flow',
      RITORNO_GENERATORE: 'Heat source return',
      PUFFER_ALTO: 'Buffer — top',
      PUFFER_BASSO: 'Buffer — bottom',
      TEMPERATURA_ESTERNA: 'Outdoor temperature',
      UMIDITA_INTERNA: 'Indoor humidity',
      LUMINOSITA: 'Light level',
      VMC: 'Ventilation (MVHR)',
      LOGICA_GENERATORE: 'Heat source logic (pin 12)',
      CIRCOLATORE: 'Pump',
      VALVOLA_DEVIATRICE: 'Diverter valve',
      COMANDO_GENERATORE: 'Heat source command',
    },
    pin12: {
      1: 'Heat source ON/OFF on aggregate zone demand',
      2: 'Modulating pump PWM, proportional to open zones',
      3: 'Modulating heat source PWM on flow temperature error',
      4: 'Hot water / heating diverter valve command',
      5: 'Pump ON/OFF, no modulation',
      6: 'External heat source enable (dry contact)',
    },
  },
  comune: {
    conferma: 'Confirm',
    annulla: 'Cancel',
    salva: 'Save',
    chiudi: 'Close',
    indietro: 'Back',
    si: 'Yes',
    no: 'No',
    caricamento: 'Loading…',
    errore: 'Something went wrong.',
    ok: 'OK',
    riprova: 'Retry',
    apri: 'Open',
    attivo: 'On',
    spento: 'Off',
    adesso: 'now',
    faMinuti: '{{n}} min ago',
    faOre: '{{n}} h ago',
    faGiorni: '{{n}} days ago',
    menu: 'Menu',
  },
  giorni: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  giorniBrevi: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  sorgenti: {
    ta: 'Third-party controller with a documented interface: read and commanded locally.',
    fv: 'Solar inverter via the manufacturer’s cloud: production, battery and consumption.',
    pdc: 'Heat pump via a shared platform: read-only until command is verified per model.',
    termostati: 'Branded thermostats via the manufacturer’s cloud: read-only.',
    emacomfort: 'Ema Comfort controller on LAN: read and command.',
    tipo: { centralina: 'Controller', fotovoltaico: 'Solar', pompa_di_calore: 'Heat pump', termostati: 'Thermostats' },
    comandabile: 'Read and command',
    solaLettura: 'Read only',
  },
  simulatore: {
    titolo: 'Simulator',
    sottotitolo: 'Compose a plant configuration and open the app on it, without installing it anywhere.',
    datiSimulati: 'Simulated data',
    avvertenza:
      'Everything shown here is simulated. Opening a configuration makes the app show the plant composed above instead of the real one for this session; “Leave the simulator” returns to real data.',
    impianto: 'Plant',
    centralina: 'Controller',
    centralinaNota:
      'Our own controller accepts commands; a third-party system is read through a connector and accepts none.',
    centralinaPropria: 'Ema Comfort',
    centralinaTerzi: 'Third party (read-only)',
    zone: 'Zones',
    zoneConteggio: '{{count}} zone',
    zoneConteggio_other: '{{count}} zones',
    generatori: 'Heat sources',
    generatoriNota: 'One to four, cascaded in the order below.',
    servizi: 'Services',
    sanitario: 'Domestic hot water',
    vmc: 'Ventilation (MVHR)',
    sondaPuffer: 'Buffer tank probe',
    sonde: 'Plant probes',
    sondeNota:
      'A missing probe is not a missing value: the reading it takes disappears from the interface rather than sitting empty.',
    sondeCentrale: 'Plant-room probes',
    sondaEsterna: 'Outdoor probe',
    umidita: 'Humidity probe',
    fotovoltaico: 'Solar PV',
    fvAssente: 'None',
    fvSenzaBatteria: 'Without battery',
    fvConBatteria: 'With battery',
    riepilogo: 'Summary',
    canali: 'Channels',
    comandi: 'Commands',
    comandiSi: 'Accepted',
    comandiNo: 'Refused (read-only)',
    link: 'Configuration link',
    linkNota:
      'The whole configuration lives in the address: this link reopens exactly this configuration, on another machine too.',
    apri: 'Open the panel',
    copia: 'Copy the link',
    copiato: 'Link copied',
    ripristina: 'Reset',
    esci: 'Leave the simulator',
    inCorso: 'Simulator running',
    tornaAiDatiReali: 'Back to real data',
  },
  demo: {
    insegna: {
      etichetta: 'Demo',
      titolo: 'Simulated plant',
      nota: 'This strip is ours: the app is the one inside the screen.',
      banco: 'Change the plant',
      /* The bench lives on the product's own site: that is where a plant is
         composed and the area to enter it from is chosen. Absolute, because
         from here we are leaving the app's domain. It moved off
         www.puglieseweb.com/products/ema-comfort with the rest of the page
         ([ADR-090]). The old path still resolves, but as a meta-refresh
         page (an Astro config redirect in a static build is not an HTTP 301),
         and a meta refresh does NOT carry the fragment — a stale bundle
         following the old link lands at the top of the page instead of at the
         bench, which is the dead end this sign exists to prevent. */
      bancoUrl: 'https://www.emacomfort.com/#demo',
    },
  },
  note: {
    'io-map.A3': 'Two bathrooms on a single sensor: to be confirmed with the client (Q2b).',
    'io-map.A6': 'Not specified in the schematic: probably spare, to be confirmed.',
    'io-map.A7': 'Also the expansion point: a multiplexer on A7 adds zones.',
    'io-map.A15': 'Presumed use: automatic shading (phase 3).',
    'ecobel.HP': 'Command via the manufacturer’s cloud: to be verified per model.',
  },
};
